import React, { Component } from "react";
import PropTypes from "prop-types";
import Button from "upn-component-button";
import Label from "upn-component-label";
import { css } from "emotion";
import styled from "react-emotion";

//Container wrapper element styling
const alignment = {
  left: css`
    margin: 0 auto 0 0;
  `,
  center: css`
    margin: 0 auto 0 auto;
  `,
  right: css`
    margin: 0 0 0 auto;
  `,
  none: css`
    margin: 0;
  `
};

const baseContainerStyles = css`
  border-color: #ccc;
  border-width: 1px;
  border-radius: 5pt;
  padding: 0;
  width: 100%;
`;

function borderStyle(border) {
  if (border) {
    return css`
      background: #fafafa;
      border: solid;
    `;
  } else {
    return css`
      background: none;
      border: none;
    `;
  }
}

//Title and Header type containers
const DivContainer = styled("div")`
  ${({ align }) => alignment[align]};
  ${({ border }) => borderStyle(border)};
  ${baseContainerStyles};
`;

//Legend type container
const FieldsetContainer = styled("fieldset")`
  ${({ align }) => alignment[align]};
  ${({ border }) => borderStyle(border)};
  ${baseContainerStyles};
  display: block;
  & > div {
    padding: 0 0.5em 0.5em 1.7em;
  }
`;

const wrapperTypes = {
  title: DivContainer,
  heading: DivContainer,
  legend: FieldsetContainer
};

//Title sub-component parts.
//Styled legend Emotion component for the Title sub-component.
const Legend = styled("legend")`
  margin-left: 1em;
  font-size: 1.1em;
  padding: 0.5em;
`;

//Styled h1 Emotion component for the Title
const Title = styled("h1")`
  margin: 1em 0.6em;
  font-size: 1.6em;
`;

//Styled div Emotion component for the Title sub-component.
const HeaderWrapper = styled("div")`
  line-height: 1;
  padding: 0.5em;
  background-color: #0071ce;
  background-image: linear-gradient(to bottom, #338dd8, #0071ce);
  border-radius: 5px;
  h2 {
    padding: 0.5em 1em;
    display: block;
    margin: auto;
    text-align: center;
    color: white;
    overflow-wrap: break-word;
    font-size: 1.5em;
  }
`;

const contentWrapperStyles = css`
  padding: 0.5em 1em;
`;

const FooterWrapper = styled("div")`
  padding: 0.25em 0;
`;

const messageStyles = css`
  display: block;
  margin: 0 1em 0.25em;
`;

const baseButtonStyles = css`
  display: block;
  padding: 0.6em;
  margin: 0.4em 0;
`;

const leftButtonStyles = css`
  ${baseButtonStyles};
  justify-self: start;
`;

const middleButtonStyles = css`
  ${baseButtonStyles};
  justify-self: center;
`;

const rightButtonStyles = css`
  ${baseButtonStyles};
  justify-self: end;
`;

const ButtonBox = styled("div")`
  padding: 0 1em 0.25em;
  display: grid;
  grid-template-columns: repeat(
    ${({ children }) => children.length},
    minmax(20px, auto)
  );
  grid-gap: 1em;
  > .buttonBoxItem {
    display: grid;
  }
`;

export default class Container extends Component {
  static propTypes = {
    className: PropTypes.string,
    align: PropTypes.oneOf(["left", "center", "right", "none"]),
    border: PropTypes.bool,
    children: PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.node),
      PropTypes.node,
      PropTypes.string
    ]),
    //Determine functionality
    title: PropTypes.string,
    heading: PropTypes.string,
    legend: PropTypes.string,
    //For error, success, etc messages in the footer.
    message: Label.propTypes.children,
    messageTheme: Label.propTypes.theme,
    //For buttons in the footer.
    buttonLeft: PropTypes.shape(Button.propTypes),
    buttonMiddle: PropTypes.shape(Button.propTypes),
    buttonRight: PropTypes.shape(Button.propTypes)
  };

  static defaultProps = {
    align: "none",
    border: true
  };

  getType = (title, heading, legend) => {
    let type;
    if (title) type = "title";
    if (heading) {
      type = "heading";
    } else if (legend) {
      type = "legend";
    }
    return type || "title";
  };

  render = () => {
    let {
      className,
      align,
      border,
      children,
      title,
      heading,
      legend,
      message,
      messageTheme,
      buttonLeft,
      buttonMiddle,
      buttonRight
    } = this.props;
    let classNames = [];
    if (className) classNames.push(className);
    let type = this.getType(title, heading, legend);
    let text = this.props[type];
    let ContainerWrapper = wrapperTypes[type];
    let header, footer;
    if (text) {
      if (type === "title") {
        header = <Title>{text}</Title>;
      } else if (type === "heading") {
        header = (
          <HeaderWrapper>
            <h2>{text}</h2>
          </HeaderWrapper>
        );
      } else if (type === "legend") {
        header = <Legend>{text}</Legend>;
      }
    }
    if (message || buttonLeft || buttonMiddle || buttonRight) {
      let footerContent = [];
      if (message) {
        footerContent.push(
          <Label className={messageStyles} key={0} theme={messageTheme}>
            {message}
          </Label>
        );
      }
      if (buttonLeft || buttonMiddle || buttonRight) {
        footerContent.push(
          <ButtonBox key={1}>
            <div className="buttonBoxItem">
              {buttonLeft && (
                <Button {...buttonLeft} className={leftButtonStyles} />
              )}
            </div>
            <div className="buttonBoxItem">
              {buttonMiddle && (
                <Button {...buttonMiddle} className={middleButtonStyles} />
              )}
            </div>
            <div className="buttonBoxItem">
              {buttonRight && (
                <Button {...buttonRight} className={rightButtonStyles} />
              )}
            </div>
          </ButtonBox>
        );
      }
      footer = <FooterWrapper>{footerContent}</FooterWrapper>;
    }
    if (text != null || children != null || footer != null) {
      return (
        <ContainerWrapper
          className={classNames.join(" ")}
          align={align}
          border={border}
        >
          {header}
          {children && (
            <div className={`content-wrapper ${contentWrapperStyles}`}>
              {children}
            </div>
          )}
          {footer}
        </ContainerWrapper>
      );
    } else return null;
  };
}
